import { useState } from 'react';
import { css } from '@emotion/react';
import {
  ActionTypeUpdate,
  ActorTypeWeave,
  ChangeTypeUpdateHoldMusic,
  type AuditEvent,
} from '@weave/schema-gen-ts/dist/apis/api/phone/audits/v1/api.pb';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { Button, Text } from '@frontend/design-system';
import { WEAVE_USER_LABEL } from '../../phone-system-history-config';
import { AdditionalContext, getEventCardTitle, getEventSummaryString } from '../../utils/audit-formatters';
import { PhoneSystemHistoryTrackingIds } from '../../utils/tracking-ids';
import { EventChangesDrawer } from './event-changes-drawer';
import { ListCard } from './list-card';

type EventCardProps = {
  event: AuditEvent;
  isHighlighted?: boolean;
  additionalContext?: AdditionalContext;
};

/**
 * Returns true if this event should show the expandable drawer toggle.
 */
function isDrawerEnabled(event: AuditEvent): boolean {
  if (event.action !== ActionTypeUpdate) return false; // Only UPDATE events can have changes to show in the drawer

  // TODO: Update this when finished testing. For now while testing, we will always show for Hold Music changes.
  return event.changes?.some((c) => c.type === ChangeTypeUpdateHoldMusic) ?? false;
}

/**
 * Renders a single audit event as a styled card within the history timeline.
 *
 * - Displays a title derived from the event action (update count, or created/deleted label).
 * - Shows the actor's name and email (when available) on a secondary line.
 * - For UPDATE events: shows a human-readable change summary.
 * - For eligible event types (currently Hold Music only): shows an expand/collapse
 *   toggle that reveals the EventChangesDrawer.
 */
export const EventCard = ({ event, isHighlighted, additionalContext }: EventCardProps) => {
  const { t } = useTranslation('phone');
  const [isExpanded, setIsExpanded] = useState(false);

  const isUpdate = event.action === ActionTypeUpdate;
  const changes = event.changes ?? [];
  const summaries = isUpdate ? getEventSummaryString(event, additionalContext) : '';
  const title = getEventCardTitle(event);
  // TODO: Re-enable the drawer toggle when ready. For now, we will not show the drawer toggle for any events.
  const showDrawer = false && isDrawerEnabled(event);

  return (
    <ListCard css={[eventCardStyle, isHighlighted && highlightedCardStyles]}>
      {/* Title */}
      <Text weight='bold' size='medium'>
        {title}
      </Text>

      {/* Actor line: "Name • email" */}
      {event.actor && (
        <Text size='small' color='subdued'>
          {event.actor.displayName}
          {event.actor.type === ActorTypeWeave && ` (${WEAVE_USER_LABEL})`}
          {event.actor.email && event.actor.type !== ActorTypeWeave && ` • ${event.actor.email}`}
        </Text>
      )}

      {/* 
        Change summary + expand/collapse toggle in the same row
        (Only shown for Update actions since only those have changes to summarize)
      */}
      {isUpdate && summaries.length > 0 && (
        <div css={changeSummaryRowStyle}>
          <Text size='medium' css={textWrapStyle}>
            {summaries}
          </Text>

          {showDrawer && (
            <Button
              onClick={() => setIsExpanded((prev) => !prev)}
              aria-label={isExpanded ? t('Collapse changes') : t('Expand changes')}
              css={expandButtonStyle}
              trackingId={
                isExpanded
                  ? PhoneSystemHistoryTrackingIds.detailsTray.eventCardDrawerCollapseButton
                  : PhoneSystemHistoryTrackingIds.detailsTray.eventCardDrawerExpandButton
              }
            >
              <Icon name={isExpanded ? 'caret-up-small' : 'caret-down-small'} size={8} color='subdued' />
            </Button>
          )}
        </div>
      )}

      {/* Expandable drawer */}
      {showDrawer && isExpanded && <EventChangesDrawer changes={changes} />}
    </ListCard>
  );
};

const highlightedCardStyles = css`
  border-color: var(--fabric-input-border-focus);
  background-color: var(--fabric-color-surface-selected-default);
  box-shadow: none;
`;

const eventCardStyle = css`
  width: 272px;
  display: flex;
  flex-direction: column;
  gap: var(--fabric-space-gap-xxs);
`;

const textWrapStyle = css`
  overflow: auto;
`;

const changeSummaryRowStyle = css`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--fabric-space-gap-xs);
`;

const expandButtonStyle = css`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  padding: 0;
  border: none;
  background-color: var(--colors-neutral-10);
  align-self: flex-end;
  &:hover {
    background-color: var(--colors-neutral-20);
  }

  // Center the icon inside the button
  span {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;
